import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiDollarSign,
  FiUserCheck,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiFileText,
  FiCalendar,
  FiMapPin,
  FiPhone,
  FiMail,
  FiAward,
  FiStar,
  FiExternalLink,
  FiDownload,
  FiPrinter,
  FiEdit,
  FiTrash2,
  FiRefreshCw,
  FiAlertCircle
} = FiIcons;

const ProviderClaims = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    specialty: 'all',
    dateRange: 'all',
    amountRange: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('submittedDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock data for claims with more detailed information
  const [claimsData, setClaimsData] = useState({
    pending: [
      {
        id: 1,
        providerName: 'Dr. Sarah Johnson',
        specialty: 'Orthodontist',
        submittedDate: '2023-06-15',
        claimAmount: 1250.00,
        status: 'Pending Review',
        profileViews: 142,
        location: 'New York, NY',
        email: 'sarah.j@dentalspecialists.com',
        phone: '(555) 123-4567',
        claimedDate: '2023-06-10',
        documents: [
          { name: 'License.pdf', type: 'License', status: 'Verified' },
          { name: 'Insurance.pdf', type: 'Insurance', status: 'Pending' },
          { name: 'Certification.pdf', type: 'Certification', status: 'Verified' }
        ],
        notes: 'Provider has excellent patient reviews. Submitted all required documents.',
        claimType: 'Profile Verification',
        processingTime: '3-5 business days'
      },
      {
        id: 2,
        providerName: 'Advanced Dental Care',
        specialty: 'General Dentistry',
        submittedDate: '2023-06-18',
        claimAmount: 850.00,
        status: 'Pending Review',
        profileViews: 89,
        location: 'Los Angeles, CA',
        email: 'info@advanceddental.com',
        phone: '(555) 987-6543',
        claimedDate: '2023-06-12',
        documents: [
          { name: 'License.pdf', type: 'License', status: 'Verified' },
          { name: 'BusinessLicense.pdf', type: 'Business License', status: 'Verified' }
        ],
        notes: 'New provider with promising credentials. Awaiting additional certification documents.',
        claimType: 'New Provider Registration',
        processingTime: '5-7 business days'
      },
      {
        id: 5,
        providerName: 'Dr. Emily Rodriguez',
        specialty: 'Periodontics',
        submittedDate: '2023-06-20',
        claimAmount: 1100.00,
        status: 'Pending Review',
        profileViews: 67,
        location: 'Austin, TX',
        email: 'emily.r@periodontics.com',
        phone: '(555) 567-8901',
        claimedDate: '2023-06-15',
        documents: [
          { name: 'License.pdf', type: 'License', status: 'Verified' },
          { name: 'SpecialtyCert.pdf', type: 'Specialty Certification', status: 'Pending' },
          { name: 'Insurance.pdf', type: 'Malpractice Insurance', status: 'Verified' }
        ],
        notes: 'Specialist with 10+ years experience. Documents under review.',
        claimType: 'Specialty Verification',
        processingTime: '7-10 business days'
      }
    ],
    approved: [
      {
        id: 3,
        providerName: 'Dr. Michael Chen',
        specialty: 'Pediatric Dentistry',
        submittedDate: '2023-06-10',
        claimAmount: 1500.00,
        status: 'Approved',
        profileViews: 210,
        location: 'Chicago, IL',
        email: 'michael.chen@kidsdental.com',
        phone: '(555) 456-7890',
        claimedDate: '2023-06-05',
        documents: [
          { name: 'License.pdf', type: 'License', status: 'Verified' },
          { name: 'Insurance.pdf', type: 'Insurance', status: 'Verified' },
          { name: 'Certification.pdf', type: 'Certification', status: 'Verified' },
          { name: 'PediatricCert.pdf', type: 'Pediatric Certification', status: 'Verified' }
        ],
        approvedDate: '2023-06-12',
        paymentDate: '2023-06-20',
        notes: 'Fully verified provider with excellent credentials.',
        claimType: 'Full Profile Verification',
        processingTime: 'Completed'
      },
      {
        id: 6,
        providerName: 'Bright Smiles Family Dentistry',
        specialty: 'Family Dentistry',
        submittedDate: '2023-06-08',
        claimAmount: 950.00,
        status: 'Approved',
        profileViews: 156,
        location: 'Seattle, WA',
        email: 'hello@brightsmiles.com',
        phone: '(555) 234-5678',
        claimedDate: '2023-06-03',
        documents: [
          { name: 'License.pdf', type: 'License', status: 'Verified' },
          { name: 'BusinessLicense.pdf', type: 'Business License', status: 'Verified' },
          { name: 'Insurance.pdf', type: 'Malpractice Insurance', status: 'Verified' }
        ],
        approvedDate: '2023-06-11',
        paymentDate: '2023-06-18',
        notes: 'Established practice with strong community presence.',
        claimType: 'Practice Verification',
        processingTime: 'Completed'
      }
    ],
    rejected: [
      {
        id: 4,
        providerName: 'Bright Smiles Clinic',
        specialty: 'Cosmetic Dentistry',
        submittedDate: '2023-06-05',
        claimAmount: 750.00,
        status: 'Rejected',
        profileViews: 65,
        location: 'Miami, FL',
        email: 'contact@brightsmiles.com',
        phone: '(555) 234-5678',
        claimedDate: '2023-06-01',
        documents: [
          { name: 'License.pdf', type: 'License', status: 'Verified' }
        ],
        rejectionReason: 'Incomplete documentation provided. Missing insurance certificate.',
        notes: 'Provider needs to submit complete documentation for reconsideration.',
        claimType: 'Profile Verification',
        processingTime: 'Rejected'
      }
    ]
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return FiCheckCircle;
      case 'Rejected':
        return FiXCircle;
      default:
        return FiClock;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'text-green-600 bg-green-100';
      case 'Rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getDocumentStatusColor = (status) => {
    switch (status) {
      case 'Verified':
        return 'text-green-600 bg-green-100';
      case 'Pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const handleViewDetails = (provider) => {
    setSelectedProvider(provider);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProvider(null);
  };

  const handleApproveClaim = (claimId) => {
    setClaimsData(prev => {
      const updatedClaims = { ...prev };
      const claim = updatedClaims.pending.find(c => c.id === claimId);
      
      if (claim) {
        // Remove from pending
        updatedClaims.pending = updatedClaims.pending.filter(c => c.id !== claimId);
        
        // Add to approved with new dates
        const approvedClaim = {
          ...claim,
          status: 'Approved',
          approvedDate: new Date().toISOString().split('T')[0],
          paymentDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 7 days from now
        };
        
        updatedClaims.approved.push(approvedClaim);
      }
      
      return updatedClaims;
    });
  };

  const handleRejectClaim = (claimId) => {
    setClaimsData(prev => {
      const updatedClaims = { ...prev };
      const claim = updatedClaims.pending.find(c => c.id === claimId);
      
      if (claim) {
        // Remove from pending
        updatedClaims.pending = updatedClaims.pending.filter(c => c.id !== claimId);
        
        // Add to rejected
        const rejectedClaim = {
          ...claim,
          status: 'Rejected',
          rejectionReason: claim.rejectionReason || 'Claim rejected by administrator'
        };
        
        updatedClaims.rejected.push(rejectedClaim);
      }
      
      return updatedClaims;
    });
  };

  const filteredClaims = claimsData[activeTab]?.filter(claim => {
    const matchesSearch = claim.providerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          claim.specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          claim.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSpecialty = filterOptions.specialty === 'all' || 
                             claim.specialty === filterOptions.specialty;
    
    return matchesSearch && matchesSpecialty;
  }) || [];

  // Sort claims
  const sortedClaims = [...filteredClaims].sort((a, b) => {
    if (sortOrder === 'asc') {
      return a[sortBy] > b[sortBy] ? 1 : -1;
    } else {
      return a[sortBy] < b[sortBy] ? 1 : -1;
    }
  });

  // Get unique specialties for filter dropdown
  const specialties = [...new Set(Object.values(claimsData).flat().map(claim => claim.specialty))];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="p-6 bg-gray-50 min-h-full"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div className="flex items-center space-x-4 mb-4 md:mb-0">
          <div className="p-3 bg-green-100 rounded-full">
            <SafeIcon icon={FiDollarSign} className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Provider Claims</h1>
            <p className="text-gray-500">Manage and process provider claims and profiles</p>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search claims..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 w-full md:w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button 
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SafeIcon icon={FiFilter} className="h-5 w-5 text-gray-500" />
            <span>Filters</span>
            <SafeIcon icon={FiChevronDown} className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Filter Options */}
      {showFilters && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
              <select 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={filterOptions.specialty}
                onChange={(e) => setFilterOptions({...filterOptions, specialty: e.target.value})}
              >
                <option value="all">All Specialties</option>
                {specialties.map(specialty => (
                  <option key={specialty} value={specialty}>{specialty}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="submittedDate">Submission Date</option>
                <option value="claimAmount">Claim Amount</option>
                <option value="providerName">Provider Name</option>
                <option value="profileViews">Profile Views</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <select 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <SafeIcon icon={FiClock} className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{claimsData.pending?.length || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{claimsData.approved?.length || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <SafeIcon icon={FiXCircle} className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">{claimsData.rejected?.length || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SafeIcon icon={FiDollarSign} className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">
                ${(Object.values(claimsData).flat().reduce((sum, claim) => sum + claim.claimAmount, 0)).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Claims Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {['pending', 'approved', 'rejected'].map((tab) => (
              <button
                key={tab}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab} ({claimsData[tab]?.length || 0})
              </button>
            ))}
          </nav>
        </div>

        {/* Claims Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Specialty
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedClaims.length > 0 ? (
                sortedClaims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiUserCheck} className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{claim.providerName}</div>
                          <div className="text-sm text-gray-500">{claim.location}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{claim.specialty}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {claim.submittedDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${claim.claimAmount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(claim.status)}`}>
                        <SafeIcon icon={getStatusIcon(claim.status)} className="h-3 w-3 mr-1" />
                        {claim.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(claim)}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
                        </button>
                        {activeTab === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApproveClaim(claim.id)}
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                              title="Approve Claim"
                            >
                              <SafeIcon icon={FiCheckCircle} className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRejectClaim(claim.id)}
                              className="text-red-600 hover:text-red-900 flex items-center"
                              title="Reject Claim"
                            >
                              <SafeIcon icon={FiXCircle} className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                    No claims found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Claim Details Modal */}
      {showModal && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Claim Details</h2>
                <div className="flex space-x-2">
                  <button
                    className="p-2 text-gray-400 hover:text-gray-500"
                    title="Download"
                  >
                    <SafeIcon icon={FiDownload} className="h-5 w-5" />
                  </button>
                  <button
                    className="p-2 text-gray-400 hover:text-gray-500"
                    title="Print"
                  >
                    <SafeIcon icon={FiPrinter} className="h-5 w-5" />
                  </button>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Provider Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <SafeIcon icon={FiUserCheck} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Provider Name</p>
                        <p className="text-sm text-gray-900">{selectedProvider.providerName}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiAward} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Specialty</p>
                        <p className="text-sm text-gray-900">{selectedProvider.specialty}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiMapPin} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Location</p>
                        <p className="text-sm text-gray-900">{selectedProvider.location}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiMail} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Email</p>
                        <p className="text-sm text-gray-900">{selectedProvider.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiPhone} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Phone</p>
                        <p className="text-sm text-gray-900">{selectedProvider.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Claim Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <SafeIcon icon={FiFileText} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Claim ID</p>
                        <p className="text-sm text-gray-900">#{selectedProvider.id}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiCalendar} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Submitted Date</p>
                        <p className="text-sm text-gray-900">{selectedProvider.submittedDate}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiDollarSign} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Claim Amount</p>
                        <p className="text-sm font-bold text-gray-900">${selectedProvider.claimAmount.toFixed(2)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiStar} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Claim Type</p>
                        <p className="text-sm text-gray-900">{selectedProvider.claimType}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiClock} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Processing Time</p>
                        <p className="text-sm text-gray-900">{selectedProvider.processingTime}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <SafeIcon icon={FiStar} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Status</p>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedProvider.status)}`}>
                          <SafeIcon icon={getStatusIcon(selectedProvider.status)} className="h-3 w-3 mr-1" />
                          {selectedProvider.status}
                        </span>
                      </div>
                    </div>
                    
                    {selectedProvider.approvedDate && (
                      <div className="flex items-start">
                        <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Approved Date</p>
                          <p className="text-sm text-gray-900">{selectedProvider.approvedDate}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedProvider.paymentDate && (
                      <div className="flex items-start">
                        <SafeIcon icon={FiDollarSign} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Payment Date</p>
                          <p className="text-sm text-gray-900">{selectedProvider.paymentDate}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedProvider.rejectionReason && (
                      <div className="flex items-start">
                        <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Rejection Reason</p>
                          <p className="text-sm text-red-600">{selectedProvider.rejectionReason}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Attached Documents</h3>
                  <span className="text-sm text-gray-500">{selectedProvider.documents.length} documents</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedProvider.documents.map((doc, index) => (
                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                        <SafeIcon icon={FiFileText} className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">{doc.type}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getDocumentStatusColor(doc.status)}`}>
                        {doc.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {selectedProvider.notes && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Notes</h3>
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-700">{selectedProvider.notes}</p>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                {activeTab === 'pending' && (
                  <>
                    <button 
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center"
                      onClick={() => handleRejectClaim(selectedProvider.id)}
                    >
                      <SafeIcon icon={FiXCircle} className="h-4 w-4 mr-2" />
                      Reject Claim
                    </button>
                    <button 
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                      onClick={() => handleApproveClaim(selectedProvider.id)}
                    >
                      <SafeIcon icon={FiCheckCircle} className="h-4 w-4 mr-2" />
                      Approve Claim
                    </button>
                  </>
                )}
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default ProviderClaims;